import { FocusEvent, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useCalendar } from '../../../calendar';
import { Text } from '../../../text';
import { DatePickerDialog } from '../date-picker/date-picker-dialog.component';
import { useDatePickerDialog } from '../date-picker/use-date-picker';
import { dateNavigatorContext } from './date-navigator-context';
import { DateNavigatorWrapper } from './date-navigator-wrapper';
import { DateNavigatorProviderProps } from './date-navigator.types';
import { parseSmartDate } from './helpers';

export const DateNavigatorProvider = ({
  value,
  today,
  hideNavigationButtons,
  navigationButtonHoverLabel,
  trackingIds,
  blackoutDates,
  maxDate,
  minDate,
  onBlur,
  onChange,
  onFocus,
  children,
  name,
  modifyCalendarProps,
}: DateNavigatorProviderProps & { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const [originalValue, setOriginalValue] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const formattedDateObj = useMemo(() => {
    const dateObj = dayjs(value);
    return {
      day: dateObj.format('ddd'),
      date: dateObj.format('MMM D'),
      year: dateObj.format('YYYY'),
      isValid: value ? dateObj.isValid() : true,
    };
  }, [value]);

  const handleBlur = (event?: FocusEvent<HTMLInputElement>) => {
    if (event?.target.value !== undefined && event?.target.value !== '') {
      const formattedDate = parseSmartDate(event.target.value);
      onChange({ name, value: formattedDate });
    } else if (event?.target.value === '') {
      onChange({ name, value: originalValue || value });
    }

    onBlur?.(event);
  };

  const datePickerDialogProps = useDatePickerDialog({
    placement: 'bottom',
    trigger: triggerRef,
    clickOutsideIgnoreRefs: [triggerRef],
    offsetDistance: [8, 0],
    onBlur: handleBlur,
    onFocus,
  });

  const { dialogProps, closeDialog } = datePickerDialogProps;

  const handleDateSelect = (selectedDate: string | string[]) => {
    const dateValue = Array.isArray(selectedDate) ? selectedDate[0] : selectedDate;
    onChange({ name, value: dateValue });
    closeDialog();
  };

  const handleNavigation = (num: number) => {
    const newDate = dayjs(value).add(num, 'day').format('MM/DD/YYYY');
    onChange({ name, value: newDate });
  };

  const calendarProps = useCalendar({
    value,
    dayNameFormat: 'one',
    today,
    maxDate,
    minDate,
    blackoutDates,
    onSelect: handleDateSelect,
  });

  const modifiedCalendarProps = useMemo(() => {
    if (!modifyCalendarProps || typeof modifyCalendarProps !== 'function') {
      return calendarProps;
    }
    return modifyCalendarProps(calendarProps);
  }, [calendarProps, modifyCalendarProps]);

  const showError = !formattedDateObj.isValid; // Show error only when focus is out of input

  return (
    <dateNavigatorContext.Provider
      value={{
        value,
        datePickerDialogProps,
        formattedDateObj,
        showError,
        onChange,
        setOriginalValue,
        errorElement: (
          <Text size='small' color='error' css={{ position: 'absolute', zIndex: theme.zIndex.high }}>
            {t('Invalid date format')}
          </Text>
        ),
      }}
    >
      {/* Date Selector */}
      <DateNavigatorWrapper
        trackingIds={trackingIds}
        showNavigationButtons={!hideNavigationButtons}
        navigationButtonHoverLabel={navigationButtonHoverLabel}
        handleNavigation={handleNavigation}
      >
        {children}
      </DateNavigatorWrapper>

      {/* Calendar Picker */}
      {/* @TODO: Replace `DatePickerDialog.Calendar` with new/existing component with latest design*/}
      <DatePickerDialog {...dialogProps}>
        <DatePickerDialog.Calendar {...modifiedCalendarProps} />
      </DatePickerDialog>
    </dateNavigatorContext.Provider>
  );
};
